import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useDispatch, useSelector } from 'react-redux';
import styles from '../../style/scanpack';
import globalStyles from '../../style/global';
import useWidth from '../scanToCartScanning/useWidth';
import ActionBarButtons from '../scanpack/actionBarButtons';
import OrderNumberHeader from '../scanToCartScanning/OrderNumberHeader';
import DisplayOrderNumber from '../../commons/displayOrderNumber';
import ScanPackBarcodeInput from './ScanPackBarcodeInput';
import SuggestedItemDisplay from './SuggestedItemDisplay';
import DisplayLogs from '../../commons/displayLogs';
import { setScanPackPrompt, handleReloadOrder, flushQueuedScans } from '../../actions/scanpackScanningActions';
import { updateScanScore } from '../../actions/scanScoreAction';
import { navigate } from '../root_navigation';
import upArrow from '../../../assets/up_arrow.png';
import downArrow from '../../../assets/down_arrow.png';
import MobileItemTabs from './MobileItemTabs';
import ScoringGame from '../scoringGame/index';
import useSpeakOnItemChange from './hooks/useSpeakOnItemChange';
import OrderNotesBar, {visibleOrderNotes} from '../scanpack/orderNotesBar';
import BoxChampBoxImage from '../scanpack/BoxChampBoxImage';
import {
  isBoxChampEnabled,
  getGuidedActiveBoxId,
  isGuidedOrderAwaitingLastSeal
} from './utils/boxChampGuidedSelectors';

const SuggestedItemSection = ({ popInternet = true }) => {
  const dispatch = useDispatch();
  const windowWidth = useWidth();
  const order = useSelector(state => state.scanPackScanning.currentOrder);
  const localLogs = useSelector(state => state.scanPackScanning.localLogs);
  const loading = useSelector(state => state.scanPackScanning.loading);
  const user = useSelector(state => state.user.userInfo);
  const scanToScoreEnabled = useSelector(state => state.user?.settings?.data?.scan_to_score);
  const scanPackSettings = useSelector(
    state => state.user.bothSettings?.data?.data?.scanpack_setting
  );
  const multiBoxActive = useSelector(
    state => state.user.bothSettings?.data?.data?.general_setting?.multi_box_shipments
  );
  const navigationSource = useSelector(state => state.scanPackScanning.navigationSource);
  const activeBoxIndex = useSelector(state => state.scanPackScanning.activeBoxIndex);
  const badgeVerifiedBoxIds = useSelector(
    state => state.scanPackScanning.boxChamp?.badgeVerifiedBoxIds
  );
  const boxChampEnabled = useSelector(isBoxChampEnabled);
  const guidedActiveBoxId = useSelector(getGuidedActiveBoxId);
  const awaitingLastSeal = useSelector(isGuidedOrderAwaitingLastSeal);

  const [actionBarShow, setActionBarShow] = useState(false);
  const [showSuggestedItem, setShowSuggestedItem] = useState(true);
  const [notesDismissedFor, setNotesDismissedFor] = useState(null);

  useEffect(() => {
    if (navigationSource?.from === 'orderDetailPage') setNotesDismissedFor(null);
  }, [navigationSource]);

  const isMobile = windowWidth < 900;

  const orderNotes = useMemo(
    () => visibleOrderNotes(order, scanPackSettings),
    [order, scanPackSettings]
  );
  const showOrderNotes =
    orderNotes.length > 0 && notesDismissedFor !== order?.id;

  useSpeakOnItemChange();

  const scannedCount = useMemo(() => {
    const scannedItems = order?.scanned_items;
    if (!scannedItems || scannedItems.length === 0) return 0;

    return scannedItems.reduce((total, item) => {
      if (item?.child_items?.length === 0 || item?.child_items === undefined) {
        return total + (item?.scanned_qty || 0);
      }
      return total;
    }, 0);
  }, [order?.scanned_items]);

  const lastPostedScore = useRef(0);

  const onScoreUpdate = useCallback(
    scoreData => {
      if (lastPostedScore.current === scoreData.score) return;
      lastPostedScore.current = scoreData.score;
      dispatch(updateScanScore({ score: scoreData.score }));
    },
    [dispatch]
  );

  const onRestartOrder = () => {
    dispatch(setScanPackPrompt(true, '', 'restart', '', false));
  };

  const onReloadOrder = () => {
    dispatch(handleReloadOrder());
  };

  const onAddNote = () => {
    const noteFromPacker = order?.notes_fromPacker || '';
    dispatch(setScanPackPrompt(true, '', 'note_from_packer', noteFromPacker, false));
  };

  const onOrderPress = async () => {
    if (order?.id) {
      await dispatch(flushQueuedScans());
      navigate('OrderDetail', {
        item: order,
        userData: user,
        page: 'ScanPackItem'
      });
    }
  };

  return (
    <View style={componentStyles.mainContainer}>
      {loading && (
        <View style={componentStyles.loaderOverlay}>
          <ActivityIndicator size="large" color="#fff" />
        </View>
      )}
      <View style={styles.RFPScreen}>
        <View style={globalStyles.nextItemFlex}>
          <LinearGradient
            start={{ x: 0, y: 1 }}
            end={{ x: 0, y: 0 }}
            colors={['#000000', '#00000006', '#00000006', '#000000']}
            style={[styles.actionButtonsView]}>
            {isMobile ? (
              <DisplayOrderNumber
                orderDetail={onOrderPress}
                incrementId={order?.increment_id}
                actionBarShow={actionBarShow}
                setActionBarState={() => setActionBarShow(!actionBarShow)}
                downArrow={downArrow}
                upArrow={upArrow}
                id={'orderDetail'}
              />
            ) : (
              <OrderNumberHeader
                orderIncrementId={order?.increment_id}
                onOrderPress={onOrderPress}
                marginRight={45}
                marginLeft={45}
              />
            )}

            {(!isMobile || actionBarShow) && !awaitingLastSeal && (
              <ActionBarButtons
                width={windowWidth}
                onPressRestartOrder={onRestartOrder}
                onPressGetOrderData={onReloadOrder}
                onPressAddNote={onAddNote}
              />
            )}
          </LinearGradient>

          <ScanPackBarcodeInput popInternet={popInternet} />

          <BoxChampBoxImage
            boxes={order?.boxes}
            activeBoxIndex={activeBoxIndex}
            activeBoxId={guidedActiveBoxId}
            verifiedBoxIds={badgeVerifiedBoxIds}
            boxChampEnabled={boxChampEnabled}
          />

          {scanToScoreEnabled && (
            <ScoringGame
              scannedCount={scannedCount}
              onScoreUpdate={onScoreUpdate}
              soundsEnabled={true}
            />
          )}

          {showOrderNotes && (
            <OrderNotesBar
              notes={orderNotes}
              onClose={() => setNotesDismissedFor(order?.id)}
            />
          )}

          {(!isMobile || showSuggestedItem) && (
            <>
              <SuggestedItemDisplay />
            </>
          )}
          {isMobile && (
            <MobileItemTabs showSuggestedItem={showSuggestedItem} toggleSuggestedItem={() => setShowSuggestedItem(!showSuggestedItem)} />
          )}

          {!isMobile && localLogs?.length > 0 && (
            <DisplayLogs logs={localLogs} sliceNum={14} checkMultiBoxActive={multiBoxActive} />
          )}

        </View>
      </View>
    </View>
  );
};

const componentStyles = StyleSheet.create({
  mainContainer: {
    flex: 4,
  },
  loaderOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
});

export default SuggestedItemSection;
